import React, { useState, useEffect, useCallback } from 'react';
import { Download, Search, CheckCircle2, XCircle, FileText, ExternalLink, CreditCard, Landmark, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import api, { getFileUrl } from '../../lib/api';

const STATUS_MAP = {
  pending: { label: 'Bekliyor', cls: 'bg-secondary text-muted-foreground' },
  waiting_approval: { label: 'Onay Bekliyor', cls: 'bg-amber-500/10 text-amber-600 dark:text-amber-400' },
  paid: { label: 'Ödendi', cls: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' },
  failed: { label: 'Başarısız', cls: 'bg-destructive/10 text-destructive' },
  rejected: { label: 'Reddedildi', cls: 'bg-destructive/10 text-destructive' },
};

const inputCls = 'px-3 py-2 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--gold))]';

export default function PaymentManager() {
  const [tab, setTab] = useState('payments');
  const [data, setData] = useState({ items: [], total: 0, page: 1, total_pages: 1 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusF, setStatusF] = useState('');
  const [providerF, setProviderF] = useState('');
  const [subs, setSubs] = useState([]);

  const load = useCallback(() => {
    setLoading(true);
    const params = { page, page_size: 10 };
    if (search.trim()) params.search = search.trim();
    if (statusF) params.status = statusF;
    if (providerF) params.provider = providerF;
    api.get('/admin/payments', { params }).then(r => setData(r.data)).catch(() => toast.error('Ödemeler yüklenemedi')).finally(() => setLoading(false));
  }, [page, search, statusF, providerF]);

  useEffect(() => { if (tab === 'payments') load(); }, [load, tab]);
  useEffect(() => {
    if (tab === 'subscriptions') api.get('/admin/subscriptions').then(r => setSubs(r.data)).catch(() => {});
  }, [tab]);

  const review = async (id, action) => {
    const note = window.prompt(action === 'approve' ? 'Onay notu (opsiyonel):' : 'Ret nedeni (opsiyonel):', '');
    if (note === null) return;
    try {
      await api.post(`/admin/payments/${id}/${action}`, { note });
      toast.success(action === 'approve' ? 'Ödeme onaylandı' : 'Ödeme reddedildi');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'İşlem başarısız'); }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get('/admin/payments/export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = 'odemeler.csv'; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Dışa aktarma başarısız'); }
  };

  const fmtDate = (d) => d ? String(d).slice(0, 16).replace('T', ' ') : '-';

  return (
    <div data-testid="admin-payment-manager">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-semibold">Ödeme Yönetimi</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Tüm ödemeleri inceleyin, havale onaylayın ve abonelikleri görün.</p>
        </div>
        {tab === 'payments' && (
          <button onClick={exportCsv} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-input text-sm font-medium hover:bg-secondary" data-testid="payments-export">
            <Download className="w-4 h-4" /> CSV Dışa Aktar
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="mb-5 inline-flex items-center gap-1 p-1 rounded-lg bg-secondary">
        <button onClick={() => setTab('payments')} className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium transition-colors ${tab === 'payments' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`} data-testid="tab-payments">
          <CreditCard className="w-4 h-4" /> Ödemeler
        </button>
        <button onClick={() => setTab('subscriptions')} className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium transition-colors ${tab === 'subscriptions' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`} data-testid="tab-subscriptions">
          <RefreshCw className="w-4 h-4" /> Abonelikler
        </button>
      </div>

      {tab === 'payments' && (
        <>
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input value={search} onChange={e => { setPage(1); setSearch(e.target.value); }} placeholder="Ad, e-posta, ödeme no ara..." className={`${inputCls} w-full pl-9`} data-testid="payments-search" />
            </div>
            <select value={statusF} onChange={e => { setPage(1); setStatusF(e.target.value); }} className={inputCls} data-testid="payments-status-filter">
              <option value="">Tüm Durumlar</option>
              {Object.entries(STATUS_MAP).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
            <select value={providerF} onChange={e => { setPage(1); setProviderF(e.target.value); }} className={inputCls} data-testid="payments-provider-filter">
              <option value="">Tüm Yöntemler</option>
              <option value="paytr">PayTR (Kart)</option>
              <option value="wire">Havale/EFT</option>
            </select>
          </div>

          {loading ? (
            <div className="flex justify-center py-10"><div className="w-8 h-8 border-2 border-[hsl(var(--gold))] border-t-transparent rounded-full animate-spin" /></div>
          ) : data.items.length === 0 ? (
            <div className="text-center py-16 rounded-2xl bg-card border border-border text-sm text-muted-foreground">Ödeme bulunamadı.</div>
          ) : (
            <div className="space-y-3" data-testid="payments-list">
              {data.items.map(p => {
                const st = STATUS_MAP[p.status] || { label: p.status, cls: 'bg-secondary' };
                const canReview = p.status === 'waiting_approval' || (p.provider === 'wire' && p.status === 'pending');
                return (
                  <div key={p.id} className="p-4 rounded-xl bg-card border border-border" data-testid="payment-item">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-[hsl(var(--gold)/0.1)] flex items-center justify-center shrink-0">
                        {p.provider === 'wire' ? <Landmark className="w-4.5 h-4.5 text-[hsl(var(--gold))]" /> : <CreditCard className="w-4.5 h-4.5 text-[hsl(var(--gold))]" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-sm">{p.full_name || 'Misafir'}</span>
                          <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full ${st.cls}`}>{st.label}</span>
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5 truncate">{p.description} • {p.email}</div>
                        <div className="text-[11px] text-muted-foreground font-mono mt-0.5">#{p.merchant_oid} • {fmtDate(p.created_at)}</div>
                      </div>
                      <div className="text-base font-semibold text-[hsl(var(--gold))] shrink-0">{p.amount_tl} {p.currency}</div>
                    </div>
                    {(canReview || p.receipt_path || p.admin_note) && (
                      <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-border">
                        {p.receipt_path && (
                          <a href={getFileUrl(p.receipt_path)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs text-[hsl(var(--gold))] hover:underline" data-testid="payment-receipt-link">
                            <FileText className="w-3.5 h-3.5" /> Dekontu Gör <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                        {p.admin_note && <span className="text-xs text-muted-foreground">Not: {p.admin_note}</span>}
                        <div className="flex-1" />
                        {canReview && (
                          <>
                            <button onClick={() => review(p.id, 'approve')} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-xs font-medium hover:bg-emerald-500/20" data-testid="payment-approve">
                              <CheckCircle2 className="w-3.5 h-3.5" /> Onayla
                            </button>
                            <button onClick={() => review(p.id, 'reject')} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-destructive/10 text-destructive text-xs font-medium hover:bg-destructive/20" data-testid="payment-reject">
                              <XCircle className="w-3.5 h-3.5" /> Reddet
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {data.total_pages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-6">
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="px-3 py-1.5 rounded-lg border border-input text-sm disabled:opacity-40 hover:bg-secondary">Önceki</button>
              <span className="text-sm text-muted-foreground">{page} / {data.total_pages}</span>
              <button disabled={page >= data.total_pages} onClick={() => setPage(p => p + 1)} className="px-3 py-1.5 rounded-lg border border-input text-sm disabled:opacity-40 hover:bg-secondary">Sonraki</button>
            </div>
          )}
        </>
      )}

      {tab === 'subscriptions' && (
        subs.length === 0 ? (
          <div className="text-center py-16 rounded-2xl bg-card border border-border text-sm text-muted-foreground">Aktif abonelik bulunmuyor.</div>
        ) : (
          <div className="space-y-3" data-testid="subscriptions-list">
            {subs.map(s => (
              <div key={s.id} className="flex items-center gap-4 p-4 rounded-xl bg-card border border-border" data-testid="subscription-item">
                <div className="w-9 h-9 rounded-lg bg-[hsl(var(--gold)/0.1)] flex items-center justify-center shrink-0"><RefreshCw className="w-4.5 h-4.5 text-[hsl(var(--gold))]" /></div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm">{s.first_name} {s.last_name}</div>
                  <div className="text-xs text-muted-foreground">{s.product_name} • {s.email}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Sonraki Yenileme</div>
                  <div className="text-sm font-medium">{fmtDate(s.next_billing_at)}</div>
                </div>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}
